import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { isPhone } from '@helpers';

import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import AnimatedTable from '@components/settings/components/common/animatedTable';
import TouchableElement from '@components/common/touchableElement';
import Loading, { SmallLoading } from '@components/loading';

import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { useManageNCFilesList } from './hooks/useManageNCFilesList';

const NCFileBlock = ({ C, font, scale, item, selectedNCFile }) => {
  const times = [
    {
      name: 'Runtime per piece',
      value: 'runTimePerPcsSecondFormatted',
    },
    {
      name: 'Change time per piece',
      value: 'changeTimePerPcsSecondFormatted',
    },
    {
      name: 'Setup time per piece',
      value: 'setupTimeSecondFormatted',
    },
  ];
  const RenderTime = ({ name, value }) => {
    return (
      <View style={{ justifyContent: 'space-between', flexDirection: 'row', marginTop: scale(10) }}>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1} ellipsizeMode={'middle'}>
          {i18n.t(name)}
        </Text>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), paddingRight: scale(9) }]}>{item[value]}</Text>
      </View>
    );
  };
  return (
    <TouchableElement onPress={() => selectedNCFile(item)}>
      {({ boxStyle }) => (
        <View
          style={{
            backgroundColor: C('Background/Light/Field'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            marginBottom: scale(10),
            paddingHorizontal: scale(15),
            paddingVertical: scale(9),
            marginHorizontal: scale(15),
          }}
        >
          <View style={{ justifyContent: 'space-between', flexDirection: 'row' }}>
            <View style={{ width: '90%' }}>
              <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]} numberOfLines={1} ellipsizeMode={'middle'}>
                {item.ncFileName}
              </Text>
            </View>
            <View style={{ marginTop: scale(7) }}>
              <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          </View>
          {times.map((t) => (
            <RenderTime {...t} key={t.value} />
          ))}
        </View>
      )}
    </TouchableElement>
  );
};

const NCFileList = (props) => {
  const { C, ncFilesByPartId, font = initialFont, scale = initialScale, ncFilesByPartIdLoaded } = props;

  const { selectedNCFile, ncFilesByPartIdFormatted } = useManageNCFilesList({ ncFilesByPartId });

  const ArrowComponent = () => {
    return (
      <View>
        <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
      </View>
    );
  };

  if (isPhone) {
    return (
      <View style={{ backgroundColor: C('Background/Light/Field on Panel'), paddingBottom: scale(80) }}>
        <Text style={[font['Button 1'], { width: '100%', textAlign: 'center', color: C('Label/Light/Primary'), marginBottom: scale(14) }]}>
          {i18n.t('Select NC File')}
        </Text>
        {ncFilesByPartIdLoaded ? (
          <FlashList
            data={ncFilesByPartIdFormatted}
            renderItem={({ item }) => <NCFileBlock {...{ C, font, scale, item, selectedNCFile }} />}
            keyExtractor={(item) => item.ncFileId}
          />
        ) : (
          <View style={{ marginTop: scale(50) }}>
            <SmallLoading />
          </View>
        )}
        {!ncFilesByPartIdFormatted.length && ncFilesByPartIdLoaded && (
          <Text
            style={[
              {
                color: C('Label/Light/Primary'),
                fontWeight: 'normal',
                fontSize: scale(13),
                width: '100%',
                textAlign: 'center',
                marginTop: scale(50),
              },
              font['Button 2'],
            ]}
          >
            {i18n.t('No data')}
          </Text>
        )}
      </View>
    );
  }

  if (!ncFilesByPartIdLoaded) {
    return (
      <View style={{ marginTop: scale(40) }}>
        <Loading background={'transparent'} />
      </View>
    );
  }

  return (
    <View style={{ width: '100%', height: '100%' }}>
      <ScrollView contentContainerStyle={{ width: '100%', height: '100%', alignItems: 'center' }}>
        <View
          style={{
            width: '100%',
          }}
        >
          <View>
            <AnimatedTable
              {...{
                C,
                loaded: ncFilesByPartIdLoaded,
                array: ncFilesByPartIdFormatted,
                headerSize: 50,
                lineSize: 42,
                showMoreSize: 42,
                baseCount: 10,
                showMoreText: i18n.t('SHOW ALL'),
                showMoreArrow: false,
                onPress: (item) => {
                  selectedNCFile(item);
                },
                scale,
                font,
                config: [
                  {
                    key: 'ncFileName',
                    title: 'NC File',
                    sort: true,
                    width: '33%',
                    color: C('Label / Light / Primary'),
                    titleColor: C('Label / Light / Secondary'),
                    style: { marginTop: scale(6) },
                    valueFont: 'Headline',
                  },
                  {
                    key: 'runTimePerPcsSecondFormatted',
                    title: 'Runtime',
                    smallTitle: 'per piece',
                    smallTitleColor: C('Label/Light/Tertiary'),
                    sort: true,
                    width: '14%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    key: 'changeTimePerPcsSecondFormatted',
                    title: 'Change time',
                    smallTitle: 'per piece',
                    smallTitleColor: C('Label/Light/Tertiary'),
                    sort: true,
                    width: '14%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    key: 'setupTimeSecondFormatted',
                    title: 'Setup time',
                    sort: true,
                    width: '14%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    key: 'lastRanByMachineName',
                    title: 'Last machine',
                    sort: true,
                    width: '25%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    function: ArrowComponent,
                    title: '',
                  },
                ],
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default NCFileList;
